/** 会员购买与订单恢复, 仅信任服务端确认的付款结果. */
import Alpine from 'alpinejs'
import { api, request } from '../lib/api'
import { goTo, type RouterStore } from '../stores/router'
import type { AuthStore } from '../stores/auth'
import type { UiStore } from '../stores/ui'
import type { Page } from '../types/api'

interface Product { id: number; name: string; duration_days: number; price_cents: number }
interface Order {
  id: string; package_name: string; duration_days: number; amount_cents: number
  status: string; pay_type: string; pay_info: string; mode: string
  created_at: string; expires_at: string; paid_at: string | null; refund_cents: number
}
const labels: Record<string, string> = { pending: '待付款', closing: '取消确认中', paid: '已支付', canceled: '已取消', expired: '已过期', refunded: '已人工退款' }
/** 整数分转换为展示金额. */
function money(cents: number): string { return (cents / 100).toFixed(2) }

export function membershipPage() {
  return {
    products: [] as Product[], enabled: false, selected: 0, mode: 'jump', mobile: false,
    loading: false, loaded: false, creating: false, error: '', requestKey: '',
    async load(): Promise<void> {
      if (this.loading || !(Alpine.store('ui') as UiStore).purchaseEnabled) return
      this.loading = true; this.error = ''
      this.mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent))
      if (!this.mobile) this.mode = 'qrcode'
      try {
        const data = await request<{ enabled: boolean; products: Product[] }>('/membership/products')
        this.products = data.products; this.enabled = data.enabled
        if (!this.products.some(p => p.id === this.selected)) this.selected = this.products.find(p => p.duration_days === 30)?.id ?? this.products[0]?.id ?? 0
        this.loaded = true
      } catch (err) { this.error = err instanceof Error ? err.message : '套餐加载失败' }
      finally { this.loading = false }
    },
    get product(): Product | undefined { return this.products.find(p => p.id === Number(this.selected)) },
    get expectedUntil(): string {
      const user = (Alpine.store('auth') as AuthStore).user
      const base = Math.max(Date.now(), user?.member_until ? new Date(user.member_until).getTime() : 0)
      return new Date(base + (this.product?.duration_days ?? 0) * 86400000).toLocaleDateString('zh-CN')
    },
    choose(id: number): void { if (this.selected !== id) this.requestKey = ''; this.selected = id },
    async buy(): Promise<void> {
      if (this.creating || !this.enabled || !this.selected) return
      this.creating = true; this.error = ''; this.requestKey ||= crypto.randomUUID()
      try {
        const order = await request<Order>('/orders', { method: 'POST', body: JSON.stringify({ package_id: this.selected, mode: this.mode, request_key: this.requestKey }) })
        this.requestKey = ''; goTo('orders', { id: order.id })
      } catch (err) { this.error = err instanceof Error ? err.message : '下单失败, 请查询我的订单' }
      finally { this.creating = false }
    },
    orders(): void { goTo('orders') },
    back(): void { goTo('profile') },
    redeem(): void { (Alpine.store('ui') as UiStore).openRedeem() },
    money,
  }
}

export function ordersPage() {
  let timer: ReturnType<typeof setTimeout> | undefined
  let generation = 0
  let accountId: number | undefined
  let syncedPaidOrder = ''
  return {
    orders: [] as Order[], order: null as Order | null, qr: '', loading: false, loaded: false,
    busy: false, error: '', offset: 0, total: 0, cancelOpen: false,
    init(): void { document.addEventListener('visibilitychange', () => { if (!document.hidden) void this.check() }) },
    async enter(): Promise<void> {
      if (!(Alpine.store('ui') as UiStore).purchaseEnabled) return
      clearTimeout(timer)
      const version = ++generation, auth = Alpine.store('auth') as AuthStore
      if (accountId !== auth.user?.id) { this.order = null; this.orders = []; this.loaded = false; this.offset = 0; syncedPaidOrder = ''; accountId = auth.user?.id }
      this.error = ''; this.loading = true
      const id = (Alpine.store('router') as RouterStore).params['id']
      try {
        if (id) {
          const order = await request<Order>(`/orders/${encodeURIComponent(id)}`)
          if (version !== generation) return
          await this.setOrder(order); if (order.status === 'paid') await this.syncMembership(order, version); void this.check()
        } else {
          this.order = null
          const page = await request<Page<Order>>(`/orders?limit=10&offset=${this.offset}`)
          if (version !== generation) return
          this.orders = page.items; this.total = page.meta.total
        }
        this.loaded = true
      } catch (err) { if (version === generation) this.error = err instanceof Error ? err.message : '订单加载失败' }
      finally { if (version === generation) this.loading = false }
    },
    leave(): void { ++generation; clearTimeout(timer); this.qr = ''; this.cancelOpen = false },
    async syncMembership(order: Order, version: number): Promise<void> {
      if (syncedPaidOrder === order.id) return
      const user = await api.auth.me()
      const auth = Alpine.store('auth') as AuthStore
      if (version !== generation || auth.user?.id !== user.id) return
      syncedPaidOrder = order.id
      auth.user = user
    },
    async setOrder(order: Order): Promise<void> {
      this.order = order; this.qr = ''
      if (order.status === 'pending' && order.pay_type === 'qrcode' && order.pay_info) {
        const qr = await import('qrcode')
        const data = await qr.toDataURL(order.pay_info, { width: 280, margin: 2 })
        if (this.order?.id === order.id && this.order.status === 'pending') this.qr = data
      }
    },
    async check(): Promise<void> {
      if (!(Alpine.store('router') as RouterStore).is('orders') || !this.order || !['pending', 'closing'].includes(this.order.status)) return
      if (this.busy) { clearTimeout(timer); timer = setTimeout(() => void this.check(), 1000); return }
      this.busy = true
      const id = this.order.id, version = generation
      clearTimeout(timer)
      try {
        const order = await request<Order>(`/orders/${id}/refresh`, { method: 'POST' })
        if (version !== generation) return
        if (order.status !== this.order?.status || order.pay_info !== this.order?.pay_info) await this.setOrder(order)
        if (order.status === 'paid') await this.syncMembership(order, version)
        this.error = ''
      } catch (err) { if (version === generation) this.error = err instanceof Error ? err.message : '暂未确认支付, 正在重试' }
      finally {
        this.busy = false
        if (version === generation && this.order && ['pending', 'closing'].includes(this.order.status)) timer = setTimeout(() => void this.check(), 5000)
      }
    },
    async resume(): Promise<void> {
      if (!this.order || this.busy) return
      this.busy = true
      try { await this.setOrder(await request<Order>(`/orders/${this.order.id}/resume`, { method: 'POST' })); this.error = '' }
      catch (err) { this.error = err instanceof Error ? err.message : '付款信息暂未恢复, 请稍后重试' }
      finally { this.busy = false; void this.check() }
    },
    async cancel(): Promise<void> {
      if (!this.order || this.busy) return
      this.busy = true
      this.cancelOpen = false
      try { await this.setOrder(await request<Order>(`/orders/${this.order.id}/cancel`, { method: 'POST' })) }
      catch (err) { this.error = err instanceof Error ? err.message : '取消结果待确认' }
      finally { this.busy = false; void this.check() }
    },
    get jumpUrl(): string {
      const order = this.order
      return order?.status === 'pending' && ['jump', 'urlscheme'].includes(order.pay_type) && /^(https:\/\/|alipays?:\/\/)/.test(order.pay_info) ? order.pay_info : ''
    },
    async page(direction: number): Promise<void> { this.offset = Math.max(0, this.offset + direction * 10); await this.enter() },
    open(id: string): void { goTo('orders', { id }) }, list(): void { goTo('orders') },
    purchase(): void { goTo('membership') }, study(): void { goTo('home') },
    date(value: string): string { return new Date(value).toLocaleString('zh-CN') },
    status(value: string): string { return labels[value] ?? value }, money,
    statusClass(value: string): string { return value === 'paid' ? 'badge-success' : ['pending', 'closing'].includes(value) ? 'badge-warning' : 'badge-neutral' },
  }
}
